import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import { Download, Upload } from 'lucide-react';
import * as React from 'react';

import { SAVE_REFUSED, saveFile } from '@/domains/api';
import type {
  ITerritoryEntry,
  ITerritoryMappingPreview
} from '../../api/ranking-process-contracts';
import { CSV_TYPE, XLSX_TYPE, rankingProcessApi } from '../../api/ranking-process-api';
import { TableFrame } from '../../components/table-frame';
import { countText } from '../../rankings';
import { refusalMessage } from '../../refusal';
import { fileRefusal } from './import-panel';

/**
 * A territorial list's vocabulary as a mapping file (SR-319), on the Territories card: downloaded
 * by every reader (XLSX, or CSV), and uploaded by `rankings.manage` as a **preview** — survey-api
 * reads the file and writes nothing, answering the vocabulary it would make, its counts, the names
 * it adds and retires, and its problems by file row. "Use this mapping" puts the preview's
 * territories in the editor's draft, offered only where the file has no problem; the card's Save
 * writes it under `If-Match`. Nothing here reads the file.
 */
export function TerritoryMapping({
  surveyCode,
  list,
  permitted,
  version,
  busy,
  onUse
}: {
  surveyCode: string;
  list: string;
  permitted: boolean;
  /** The vocabulary's version: a preview is of the version it was read against. */
  version: number;
  /** The card is saving: no upload meanwhile. */
  busy: boolean;
  onUse: (territories: ITerritoryEntry[]) => void;
}) {
  const input = React.useRef<HTMLInputElement>(null);
  const [preview, setPreview] =
    React.useState<Nullable<{ fileName: string; answer: ITerritoryMappingPreview }>>(null);
  const [reading, setReading] = React.useState<Nullable<'upload' | 'xlsx' | 'csv'>>(null);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const reasonId = React.useId();
  // A save or a reload moves the version: the preview's Added and Retired no longer hold.
  React.useEffect(() => setPreview(null), [version]);

  const download = async (format: 'xlsx' | 'csv') => {
    setReading(format);
    setRefusal(null);
    try {
      const { file, fileName } = await rankingProcessApi.mappingFile(surveyCode, list, format);
      if (!saveFile(file, fileName ?? `${surveyCode}-${list}-territories.${format}`)) {
        setRefusal(SAVE_REFUSED);
      }
    } catch (failure) {
      setRefusal(refusalMessage(failure, 'Could not download the mapping'));
    } finally {
      setReading(null);
    }
  };

  const upload = async (file: File) => {
    setReading('upload');
    setRefusal(null);
    setPreview(null);
    try {
      const answer = await rankingProcessApi.previewMapping(surveyCode, list, file);
      setPreview({ fileName: file.name, answer });
    } catch (failure) {
      setRefusal(
        fileRefusal(failure, 'There is no such list for this survey.', 'The file could not be read')
      );
    } finally {
      setReading(null);
    }
  };

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="gap-1.5"
          disabled={reading !== null}
          onClick={() => void download('xlsx')}
        >
          <Download className="size-4" aria-hidden />
          {reading === 'xlsx' ? 'Downloading…' : 'Download the mapping'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-label="Download the mapping as CSV"
          disabled={reading !== null}
          onClick={() => void download('csv')}
        >
          {reading === 'csv' ? 'Downloading…' : 'CSV'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="gap-1.5"
          disabled={!permitted || busy || reading !== null}
          aria-describedby={permitted ? undefined : reasonId}
          onClick={() => input.current?.click()}
        >
          <Upload className="size-4" aria-hidden />
          {reading === 'upload' ? 'Reading the file…' : 'Upload a mapping'}
        </Button>
        {!permitted && (
          <span id={reasonId} className="sr-only">
            Your Team Portal roles do not include changing a list’s territories.
          </span>
        )}
        <input
          ref={input}
          type="file"
          accept={`.csv,.xlsx,${CSV_TYPE},${XLSX_TYPE}`}
          hidden
          tabIndex={-1}
          aria-label="Mapping file"
          onChange={(event) => {
            const chosen = event.target.files?.[0];
            event.target.value = '';
            if (chosen) void upload(chosen);
          }}
        />
      </div>

      {refusal && (
        <Alert variant="destructive">
          <AlertTitle>Mapping not read</AlertTitle>
          <AlertDescription>{refusal}</AlertDescription>
        </Alert>
      )}

      {preview && (
        <MappingPreview
          fileName={preview.fileName}
          answer={preview.answer}
          permitted={permitted && !busy}
          onUse={() => {
            onUse(preview.answer.territories);
            setPreview(null);
          }}
          onCancel={() => setPreview(null)}
        />
      )}
    </div>
  );
}

function MappingPreview({
  fileName,
  answer,
  permitted,
  onUse,
  onCancel
}: {
  fileName: string;
  answer: ITerritoryMappingPreview;
  permitted: boolean;
  onUse: () => void;
  onCancel: () => void;
}) {
  const { problems } = answer;
  return (
    <section
      aria-label="Mapping preview"
      className="grid gap-3 rounded-md border border-border bg-[var(--accent)] p-3 text-sm"
      data-mapping-preview
    >
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <strong>{fileName}</strong>
        <span data-mapping-counts>
          {countText(answer.territories.length)} territories · {countText(answer.rules)} rules ·{' '}
          {countText(answer.rows)} rows
        </span>
      </div>
      <Names title="Added" names={answer.added} />
      <Names title="Will be retired" names={answer.retired} />
      {problems.length > 0 && (
        <div className="grid gap-1">
          <span className="font-medium text-destructive">
            {problems.length === 1 ? '1 problem' : `${countText(problems.length)} problems`}
          </span>
          <TableFrame className="max-h-64 overflow-y-auto bg-white">
            <Table aria-label="Problems in the mapping">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Row</TableHead>
                  <TableHead className="w-32">Column</TableHead>
                  <TableHead>Problem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {problems.map((problem, index) => (
                  <TableRow key={index}>
                    <TableCell>{problem.row}</TableCell>
                    <TableCell>{problem.column ?? '—'}</TableCell>
                    <TableCell>{problem.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableFrame>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {problems.length === 0 && (
          <Button type="button" size="sm" disabled={!permitted} onClick={onUse}>
            Use this mapping
          </Button>
        )}
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </section>
  );
}

/** A line of names under its title, or "none". */
function Names({ title, names }: { title: string; names: string[] }) {
  return (
    <div>
      <span className="text-muted-foreground">{title}: </span>
      {names.length === 0 ? (
        <span className="text-muted-foreground">none</span>
      ) : (
        names.join(', ')
      )}
    </div>
  );
}
